import { lazy } from "react";
import { Navigate } from "react-router-dom";

import MyAssessmentIcon from "../../assets/SVGIcons/MyAssessmentIcon.web";
import MyCoursesIcon from "../../assets/SVGIcons/MyCoursesIcon.web";
import MyGradebookIcon from "../../assets/SVGIcons/MyGradebookIcon.web";
import MyPathIcon from "../../assets/SVGIcons/MyPathIcon.web";
import ReadIcon from "../../assets/SVGIcons/ReadIcon.web";
import ReportCardIcon from "../../assets/SVGIcons/ReportCardIcon.web";
import DashboardIcon from "../../assets/SVGIcons/DashboardIcon.web";
import { NavigationGroup, NavigationRoute } from ".";
import { isStage2TabHidden, isTeacherGradebookTabHidden } from "./tabVisibility";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import StudentProfile from "../../components/pages/teacherWorkSpace/@dashboard/@studentProfile/StudentProfile";
// Chart-bearing analytics tabs are React.lazy()'d (matching registrarRoutes.tsx)
// so @mui/x-charts + its transitive d3-shape/d3-path land ONLY behind a lazy
// boundary. A STATIC import here pulled the "charts" chunk into the eager entry
// chunk (route configs load at bootstrap), forcing ~42 kB-gz of charting onto
// first paint of every page. Keep these lazy; see vite.config.ts "charts" note.
const AnalyticsProgressTab = lazy(
  () =>
    import(
      "../../components/pages/teacherWorkSpace/@dashboard/@studentProfile/analyticsTabs/AnalyticsProgressTab"
    ),
);
const AnalyticsPerformanceTab = lazy(
  () =>
    import(
      "../../components/pages/teacherWorkSpace/@dashboard/@studentProfile/analyticsTabs/AnalyticsPerformanceTab"
    ),
);

const ActivityStatusManagerPage = lazy(
  () => import("../../components/pages/teacherWorkSpace/courses/ActivityStatusManagerPage"),
);

// LAZY ROUTES
// Course Management hub — modern Supabase implementation, shared with the
// parent-teacher workspace via a `basePath` prop. Replaces the retired
// @course-management tree, which ran on the decommissioned "activities"
// backend (VITE_API_URL_ACTIVITIES, no longer configured) and so could not
// load or deep-copy courses for teachers.
const CourseManagement = lazy(
  () => import("@components/pages/parentTeacherWorkSpace/course-management/CourseManagement"),
);
const CourseManagementTeacherCourses = lazy(
  () => import("@components/pages/parentTeacherWorkSpace/course-management/tabs/TeacherCourses"),
);
const CourseManagementCatalogs = lazy(
  () => import("@components/pages/parentTeacherWorkSpace/course-management/tabs/Catalogs"),
);
const CourseManagementCatalogCourses = lazy(
  () => import("@components/pages/parentTeacherWorkSpace/course-management/tabs/CatalogCourses"),
);
const CourseManagementCourseJourney = lazy(
  () => import("@components/pages/parentTeacherWorkSpace/course-management/CourseJourney"),
);
const CourseManagementCourseRequests = lazy(
  () => import("@components/pages/parentTeacherWorkSpace/course-management/CourseRequests"),
);

const TeacherCourses = lazy(
  () => import("../../components/pages/teacherWorkSpace/courses/TeacherCourses"),
);
const CourseJourney = lazy(
  () => import("../../components/pages/teacherWorkSpace/courses/TeacherCourseJourney"),
);
const TeacherProfile = lazy(
  () => import("../../components/pages/teacherWorkSpace/profile/TeacherProfile"),
);
const AllStudentProgressPerformance = lazy(
  () => import("../../components/pages/teacherWorkSpace/@dashboard/AllStudentProgressPerformance"),
);
const AllAssignedAssestments = lazy(
  () => import("../../components/pages/teacherWorkSpace/@dashboard/AllAssignedAssestments"),
);
const TeacherStudents = lazy(
  () => import("../../components/pages/teacherWorkSpace/@students/TeacherStudents"),
);
const TeacherTutorControls = lazy(() => import("../../components/pages/teacherWorkSpace/tutorControls/TeacherTutorControls"));
const TeacherCalendar = lazy(
  () => import("../../components/pages/teacherWorkSpace/calendar/TeacherCalendar"),
);
const TeacherAssessment = lazy(
  () => import("../../components/pages/teacherWorkSpace/assessment/TeacherAssessment"),
);
// AI-native grading screen (Snap & grade · Grades · Attendance & Behaviors ·
// Summary) — a teacher-side shell over the SAME role-generic grading leaves the
// parent-teacher workspace uses, scoped to the teacher's own class rosters
// (class_teachers → class_students). Deliberately a new shell, NOT the PT
// GradingScreen reused in place (PT-twin-lag pattern).
const TeacherGrading = lazy(
  () => import("../../components/pages/teacherWorkSpace/grading/TeacherGradingScreen"),
);

// f3cd: the single role-aware gradebook surface + (reuse the deck-matching PT
// detail for the teacher's student-centric per-student gradebook).
const RoleAwareGradeBooks = lazy(() => import("@components/shared/gradebooks/RoleAwareGradeBooks"));
const SharedGradeBookDetails = lazy(() => import("@components/pages/parentTeacherWorkSpace/gradebooks/GradeBookDetails"));
const SharedGradeBookGradesTab = lazy(() => import("@components/pages/parentTeacherWorkSpace/gradebooks/tabs/GradeBookGradesTab"));
const SharedGradeBookAttendanceTab = lazy(() => import("@components/pages/parentTeacherWorkSpace/gradebooks/tabs/GradeBookAttendanceTab"));
const TeacherDashboard = lazy(
  () => import("../../components/pages/teacherWorkSpace/@dashboard/TeacherDashboard"),
);

const TeacherCoursesCourseTab = lazy(
  () => import("../../components/pages/teacherWorkSpace/courses/partials/tabs/CourseTab"),
);
const TeacherCoursesParticipantsTab = lazy(
  () => import("../../components/pages/teacherWorkSpace/courses/partials/tabs/ParticipantsTab"),
);
const TeacherAssessmentAllTab = lazy(
  () => import("../../components/pages/teacherWorkSpace/assessment/partials/tabs/AllTab"),
);
const TeacherAssessmentAssignedTab = lazy(
  () => import("../../components/pages/teacherWorkSpace/assessment/partials/tabs/AssignedTab"),
);
const TeacherAssessmentGradedTab = lazy(
  () => import("../../components/pages/teacherWorkSpace/assessment/partials/tabs/GradedTab"),
);
const TeacherAssessmentToGradeTab = lazy(
  () => import("../../components/pages/teacherWorkSpace/assessment/partials/tabs/ToGradeTab"),
);
const TeacherPendingExtensions = lazy(
  () => import("../../components/pages/teacherWorkSpace/extensions/PendingExtensions"),
);
const StudentLessonView = lazy(
  () => import("../../components/stubs/StudentLessonView"),
);
// KnowledgeGraphPage import removed per Chronicle boundary (2026-04-15).
// Component preserved at components/pages/studentWorkSpace/knowledgeGraph/
// as Chronicle incubator material. Mastery Map is a Chronicle-exclusive feature.

const BuildCourseWithAI = lazy(
  () => import("../../components/pages/adminWorkSpace/course-management/ai/BuildCourseWithAI"),
);

// Chronicle integration — iframe pages mounted when org.chronicle_enabled.
// See docs/superpowers/specs/2026-04-27-chronicle-integration-design.md.
const ChronicleToday = lazy(() => import("@components/pages/chronicle/teacher/TodayPage"));
const ChronicleReview = lazy(() => import("@components/pages/chronicle/teacher/ReviewPage"));

// Tab reveal (go-live visibility gates). Hides these nav tabs from the
// production sidebar while keeping the routes registered, so a direct link /
// deep link still resolves. VITE_STAGE2_TABS_UNLOCK is set to "true" only on
// the FE Vercel Preview (staging) env — the production build (var absent)
// keeps hidden:true. Mirrors VITE_PT_UNLOCK_TABS in parentTeacherRoutes.tsx.
//
// VITE_TEACHER_GRADEBOOK_UNLOCK is the NARROW gate: it reveals ONLY the
// "grading" and "grade book" tabs below, so production can ship teacher
// grading without also exposing the unfinished extensions / course-management
// tabs that flipping STAGE2 would drag along. See ./tabVisibility.ts (the
// parsing rule is shared and unit-tested there).
const TEACHER_GRADEBOOK_TAB_HIDDEN = isTeacherGradebookTabHidden(import.meta.env);
const STAGE2_TAB_HIDDEN = isStage2TabHidden(import.meta.env);

export const teacherRoutes: NavigationRoute[] = [
  {
    path: "courses/:course_id",
    label: "Course Journey",
    component: <Navigate to="/teacher/courses" />,
  },
  {
    path: "courses/:course_id/journey",
    label: "Course Journey",
    component: <Navigate to="/teacher/courses" />,
  },
  {
    path: "courses/:course_id/journey/:class_id",
    label: "Course Journey",
    component: <CourseJourney />,
  },
  {
    path: "courses/:course_id/activities",
    label: "Activity Status Manager",
    component: <ActivityStatusManagerPage />,
  },
  {
    path: "profile",
    label: "profile",
    component: <TeacherProfile />,
  },
  {
    path: "students/:student_id",
    label: "students",
    component: <StudentProfile />,
    children: [
      {
        index: true,
        component: <Navigate to="progress" />,
      },
      {
        path: "progress",
        component: <AnalyticsProgressTab />,
      },
      {
        path: "performance",
        component: <AnalyticsPerformanceTab />,
      },
    ],
  },
  // "students/:student_id/knowledge-graph" route removed per Chronicle boundary
  // (2026-04-15). Becomes a Chronicle section feature when Chronicle is enabled.
  {
    path: "students/progress&performace",
    label: "students progress & performace",
    component: <AllStudentProgressPerformance />,
  },
  {
    path: "student/:student_id/lesson/:lesson_id",
    label: "student lesson",
    component: <StudentLessonView />,
  },
  {
    path: "students/assigned-assesment",
    label: "Assigned Assesment",
    component: <AllAssignedAssestments />,
  },
  {
    path: "course-management/requests",
    component: <Navigate to="for-approval" />,
  },
  {
    path: "course-management/catalogs/:catalog_id",
    label: "Catalog Courses",
    component: <CourseManagementCatalogCourses basePath="/teacher" />,
  },
  {
    path: "course-management/:course_id/journey/:topic_id?/:lesson_id?/:activity_id?",
    label: "Course Journey",
    component: <CourseManagementCourseJourney basePath="/teacher" />,
  },
  {
    path: "course-management/requests/:status",
    label: "Course Requests",
    component: <CourseManagementCourseRequests basePath="/teacher" />,
  },
  { path: "grade-book/:gradebook_id/*", component: <Navigate to="/teacher/grade-book" replace /> },
  {
    // f3cd: student-centric per-student gradebook for the teacher roster's row
    // links — reuses the deck-matching shared (PT) detail + tabs.
    path: "gradebooks/:student_id/:grade_level_id",
    label: "grade-book",
    component: <SharedGradeBookDetails />,
    children: [
      { index: true, component: <Navigate to="grades" /> },
      { path: "grades", component: <SharedGradeBookGradesTab /> },
      { path: "attendance-behavior", component: <SharedGradeBookAttendanceTab /> },
    ],
  },
  {
    path: "course-management/ai",
    label: "Build course with AI",
    component: <BuildCourseWithAI />,
  },
  // Chronicle iframe routes — degrade gracefully when not configured.
  {
    path: "chronicle/today",
    label: "Today",
    component: <ChronicleToday />,
  },
  {
    path: "chronicle/review",
    label: "Review Queue",
    component: <ChronicleReview />,
  },
];

export const teacherNavigations: NavigationGroup[] = [
  {
    title: "Getting Started",
    routes: [
      {
        label: "dashboard",
        component: <TeacherDashboard />,
        icon: <DashboardIcon fontSize="small" />,
        path: "dashboard",
      },
    ],
  },
  {
    title: "Work Space",
    routes: [
      {
        path: "students",
        label: "students",
        component: <TeacherStudents />,
        icon: <ReadIcon fontSize="medium" />,
      },
      {
        // AI Tutor controls: which courses / vectorized textbooks feed each
        // student's tutor path (Chronicle-backed).
        path: "tutor-controls",
        label: "Orbit",
        component: <TeacherTutorControls />,
        icon: <AutoAwesomeIcon fontSize="small" />,
        // Pre-release: hidden in prod, shown where the tutor backend is enabled.
        requiresAiTutor: true,
      },
      {
        path: "courses",
        label: "courses",
        component: <TeacherCourses />,
        icon: <MyCoursesIcon fontSize="medium" />,
        children: [
          {
            index: true,
            component: <TeacherCoursesCourseTab />,
          },
          {
            path: "participants",
            component: <TeacherCoursesParticipantsTab />,
          },
        ],
      },
      {
        path: "calendar",
        label: "calendar",
        component: <TeacherCalendar />,
        icon: <MyPathIcon fontSize="medium" />,
      },
      {
        path: "assessment",
        label: "assessment",
        component: <TeacherAssessment />,
        icon: <MyAssessmentIcon fontSize="medium" />,
        children: [
          {
            index: true,
            component: <TeacherAssessmentAllTab />,
          },
          {
            path: "assigned",
            component: <TeacherAssessmentAssignedTab />,
          },
          {
            path: "graded",
            component: <TeacherAssessmentGradedTab />,
          },
          {
            path: "to-grade",
            component: <TeacherAssessmentToGradeTab />,
          },
        ],
      },
      {
        // AI-native grading (Snap & grade · Grades · Attendance & Behaviors ·
        // Summary) for the teacher's own class rosters. Distinct from "grade
        // book" below (the roster-level gradebook table): this is the
        // per-student enter/review-grades flow the PT workspace already had.
        path: "grade",
        label: "grading",
        component: <TeacherGrading />,
        // ReportCardIcon hard-codes width/height 40x53 in its own sx; pass an sx
        // (spread last, so it wins) to size it to 20x20 like the other icons.
        icon: <ReportCardIcon sx={{ width: 20, height: 20 }} />,
        hidden: TEACHER_GRADEBOOK_TAB_HIDDEN,
      },
      {
        path: "grade-book",
        label: "grade book",
        component: <RoleAwareGradeBooks basePath="/teacher" canSubmit />,
        icon: <MyGradebookIcon fontSize="medium" />,
        hidden: TEACHER_GRADEBOOK_TAB_HIDDEN,
      },
      {
        path: "extensions",
        label: "extensions",
        component: <TeacherPendingExtensions />,
        icon: <MyPathIcon fontSize="medium" />,
        hidden: STAGE2_TAB_HIDDEN,
      },
    ],
  },
  {
    title: "Manage Hub",
    routes: [
      {
        path: "course-management",
        label: "course management",
        component: <CourseManagement />,
        icon: <MyCoursesIcon fontSize="medium" />,
        hidden: STAGE2_TAB_HIDDEN,
        children: [
          {
            index: true,
            component: <Navigate to="courses" />,
          },
          {
            path: "courses",
            label: "Teacher Courses",
            component: <CourseManagementTeacherCourses basePath="/teacher" />,
          },
          {
            path: "catalogs",
            label: "Catalogs",
            component: <CourseManagementCatalogs basePath="/teacher" />,
          },
          {
            path: "catalogs/:catalog_id",
            label: "Catalogs",
            component: <CourseManagementCatalogCourses basePath="/teacher" />,
          },
        ],
      },
    ],
  },
];

export default teacherNavigations;
